import React from 'react';

import { Avatar, Box, Group, Text, UnstyledButton } from '@mantine/core';
import { IconAward } from '@tabler/icons-react';
import { getAvatarColor } from 'utils/colors';

export interface LeaderboardEntry {
  athleteId: string;
  rank: number;
  initials: string;
  name: string;
  display: string;
}

// Gold/silver/bronze aren't part of the Mantine palette, so these are hardcoded rather
// than pulled from theme.colors — same reasoning as AthleteStatusBadge's own color map.
const MEDAL_COLOR_BY_RANK: Record<number, string> = {
  1: '#F9C22E',
  2: '#9CA3AF',
  3: '#C07840',
};

interface LeaderboardRowProps {
  entry: LeaderboardEntry;
  highlighted?: boolean;
  onClick?: (athleteId: string) => void;
  enlarged?: boolean;
}

const LeaderboardRow = ({
  entry,
  highlighted = false,
  onClick,
  enlarged = false,
}: LeaderboardRowProps): JSX.Element => {
  const medalColor = MEDAL_COLOR_BY_RANK[entry.rank];
  const isInteractive = !!onClick;
  const rankSlotSize = enlarged ? 28 : 20;
  const iconSize = enlarged ? 26 : 20;
  const avatarSize = enlarged ? 36 : 26;
  const textSize = enlarged ? 'md' : 'sm';

  const content = (
    // A plain flex Box, not Group — Group forces `flex-grow: 0` on every direct child via
    // its own `& > *` rule, which silently overrides the name's `flex: 1` below.
    <Box
      data-athlete-id={entry.athleteId}
      sx={theme => ({
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.sm,
        boxSizing: 'border-box',
        width: '100%',
        paddingBlock: enlarged ? 12 : 8,
        paddingInline: enlarged ? 14 : 10,
        backgroundColor: highlighted ? theme.colors.green[0] : theme.white,
        border: `1px solid ${highlighted ? theme.colors.green[2] : theme.colors.gray[2]}`,
        borderRadius: theme.radius.md,
        ...(isInteractive && {
          '&:hover': {
            backgroundColor: highlighted ? theme.colors.green[1] : theme.colors.gray[0],
          },
        }),
      })}
    >
      <Group spacing={0} noWrap position='center' sx={{ width: rankSlotSize, flexShrink: 0 }}>
        {medalColor ? (
          <IconAward size={iconSize} color={medalColor} />
        ) : (
          <Text size={textSize} weight={700} color='dimmed'>
            {entry.rank}
          </Text>
        )}
      </Group>
      <Avatar size={avatarSize} radius='xl' color={getAvatarColor(entry.athleteId)}>
        {entry.initials}
      </Avatar>
      <Text size={textSize} weight={500} sx={{ flex: 1, minWidth: 0 }} truncate>
        {entry.name}
      </Text>
      <Text size={textSize} weight={700} sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
        {entry.display}
      </Text>
    </Box>
  );

  if (!onClick) return content;

  return (
    <UnstyledButton onClick={() => onClick(entry.athleteId)} sx={{ width: '100%' }}>
      {content}
    </UnstyledButton>
  );
};

export default LeaderboardRow;
